import API_URL from './config';
import { encodeHeartbeatBody } from './heartbeatCodec';

/** Must stay in sync with routes/analytics.js MAX_BATCH */
const MAX_BATCH = 50;
const FLUSH_MS = 60000;

const HEARTBEAT_URL = `${API_URL}/api/analytics/heartbeat`;

const queue = [];
let flushIntervalId = null;
let flushInProgress = false;
let unloadListenersRegistered = false;

function stopFlushInterval() {
  if (flushIntervalId != null) {
    clearInterval(flushIntervalId);
    flushIntervalId = null;
  }
}

function startFlushInterval() {
  if (flushIntervalId != null) return;
  flushIntervalId = setInterval(() => {
    void performFlush();
  }, FLUSH_MS);
}

/**
 * @param {{ h: string }} wire
 * @param {string} token
 * @param {{ keepalive?: boolean }} [options]
 */
function postHeartbeatWire(wire, token, { keepalive = false } = {}) {
  return fetch(HEARTBEAT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(wire),
    ...(keepalive ? { keepalive: true } : {}),
  });
}

function flushQueueWithKeepalive(token) {
  while (queue.length > 0) {
    const batch = queue.splice(0, MAX_BATCH);
    const wire = { h: encodeHeartbeatBody({ events: batch }) };
    void postHeartbeatWire(wire, token, { keepalive: true }).catch(() => {});
  }
}

function registerUnloadFlush() {
  if (unloadListenersRegistered || typeof window === 'undefined') return;
  unloadListenersRegistered = true;

  const flushPendingWithKeepalive = () => {
    stopFlushInterval();
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token || queue.length === 0) return;
    flushQueueWithKeepalive(token);
  };

  window.addEventListener('pagehide', flushPendingWithKeepalive);
  window.addEventListener('beforeunload', flushPendingWithKeepalive);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushPendingWithKeepalive();
    }
  });
}

registerUnloadFlush();

async function performFlush() {
  if (flushInProgress) return;
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (!token) {
    queue.length = 0;
    stopFlushInterval();
    return;
  }

  flushInProgress = true;
  try {
    while (queue.length > 0) {
      const batch = queue.splice(0, MAX_BATCH);
      const wire = { h: encodeHeartbeatBody({ events: batch }) };
      await postHeartbeatWire(wire, token).catch(() => {});
    }
  } finally {
    flushInProgress = false;
    if (queue.length === 0) {
      stopFlushInterval();
    } else {
      void performFlush();
    }
  }
}

/**
 * Fire-and-forget activity event (requires logged-in user).
 * Events are batched and sent every few seconds while the queue is non-empty.
 */
export function trackEvent(eventType, { scope = 'global', context = {}, payload = {} } = {}) {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (!token) return;

  registerUnloadFlush();

  queue.push({
    eventType,
    scope,
    context: context || {},
    payload: payload || {},
  });
  startFlushInterval();
}
